import PaperDownloadIcon from 'assets/images/icons/PaperDownloadIcon.svg'
import { AxiosError } from 'axios'

import { useAppSelector } from 'appState/store'
import { LoadingAnimation } from '@outbound-ai/ursa'
import { reveal } from 'components/Theme/animations'
import { apiPaths } from 'configs/apiConfigs'
import { getCallResource } from 'features/calls/callsApi'
import { useCallTranscript } from 'features/calls/callsHooks'
import { CallChatDisplay } from 'features/calls/components'
import { CallParticipantTypeEnum, CallResourcesEnum, CallTypeToJobTypeEnum, ICall } from 'features/calls/types'
import { IClaim } from 'features/claims/types'
import { saveAs } from 'file-saver'
import { useState } from 'react'
import { FaChevronDown, FaChevronUp, FaCopy, FaFileAlt, FaFileCode } from 'react-icons/fa'
import { Button } from '@outbound-ai/ursa'
import styled, { css } from 'styled-components'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { dotNetInteger32 } from 'utils'
import { handleApiRequestWithStates } from 'utils/api'
import { errorHandler } from 'utils/errors/errors'
import { isOutboundUser } from 'utils/Utils'
import { PostCallAudioPlayer } from './PostCallAudioPlayer'
import { formatTranscriptMessage } from 'features/calls/utils/transcriptFormatter'

interface IProps {
  call: ICall
  claim: IClaim
  styles?: any
}

export const PostCallChat = ({ call, claim, styles }: IProps) => {
  // local state
  const [isLoadingRecording, setIsLoadingRecording] = useState(false)
  const [isLoadingPlayAudio, setIsLoadingPlayAudio] = useState(false)
  const [recordingSrc, setRecordingSrc] = useState('')
  const [showAudioPlayer, setShowAudioPlayer] = useState(false)
  const [recording, setRecording] = useState<null | Blob>(null)

  const { user } = useAppSelector((state) => state.auth)

  // API
  const callResourceApi = (resource: CallResourcesEnum) =>
    apiPaths.callResource(call.jobId, resource, `?Limit=${dotNetInteger32}`)

  const { data: chatTranscript } =
    call.jobType != CallTypeToJobTypeEnum.APIIntegration
      ? useCallTranscript(call.jobId, {
          onError: (err: AxiosError) => errorHandler.handleError(err, ErrorMessagesEnum.CallTranscript),
        })
      : { data: null }

  // handlers
  const handleDownloadRecording = async () => {
    if (!recording) {
      const { data, error } = await handleApiRequestWithStates(
        () => getCallResource(callResourceApi(CallResourcesEnum.recording), CallResourcesEnum.recording),
        setIsLoadingRecording,
      )
      if (error) {
        errorHandler.handleError(error)
        return
      }

      if (data) {
        const record = new Blob([data], { type: 'audio/wav' })
        saveAs(record, `recording-${call.jobId}.wav`)
        setRecording(record)
        setRecordingSrc(URL.createObjectURL(record))
      }
    } else {
      saveAs(recording, `recording-${call.jbId}.wav`)
    }
  }

  const handlePlayAudio = async () => {
    if (call.jobType === CallTypeToJobTypeEnum.APIIntegration) return
    if (!recording) {
      const { data, error } = await handleApiRequestWithStates(
        () => getCallResource(callResourceApi(CallResourcesEnum.recording), CallResourcesEnum.recording),
        setIsLoadingPlayAudio,
      )

      if (error) {
        errorHandler.handleError(error, ErrorMessagesEnum.GetAudio)
        return
      }

      if (data) {
        const record = new Blob([data], { type: 'audio/wav' })
        setRecording(record)
        setRecordingSrc(URL.createObjectURL(record))
        setShowAudioPlayer((prev) => !prev)
      }
    } else {
      setRecordingSrc(URL.createObjectURL(recording))
      setShowAudioPlayer((prev) => !prev)
    }
  }

  const handleDownloadTranscript = async () => {
    const filteredTranscript = chatTranscript.data.filter(
      (item: any) => item.participantType != CallParticipantTypeEnum.DoubleTranscription,
    )
    const filteredRecord = new Blob([JSON.stringify(filteredTranscript)], { type: 'application/json' })
    const record = new Blob([JSON.stringify(chatTranscript)], { type: 'application/json' })

    saveAs(!isOutboundUser(user!) ? filteredRecord : record, `transcript-${call.jbId}.json`)
  }

  // variables
  const callStartTime = call.jobStarted || call.jobQueued || call.jobRequested

  const formatTranscriptToText = () => {
    let transcriptText = ''
    chatTranscript.data.forEach((item: any) => {
      if (!isOutboundUser(user!) && item.participantType === CallParticipantTypeEnum.DoubleTranscription) {
        return
      }

      const { messageText, participantTitle, timestamp, eventType } = formatTranscriptMessage(
        item,
        call,
        claim,
        callStartTime,
        user || undefined,
      )
      transcriptText += `${timestamp} - ${participantTitle}: ${eventType === true ? '[Event] ' : ''}: ${messageText}\n`
    })
    return transcriptText
  }

  const handelDownloadTranscriptTxt = async () => {
    const transcriptText = formatTranscriptToText()
    const record = new Blob([transcriptText], { type: 'text/plain' })
    saveAs(record, `transcript-${call.jbId}.txt`)
  }

  const handelCopyTrasnscript = async () => {
    const transcriptText = formatTranscriptToText()
    navigator.clipboard.writeText(transcriptText)
  }

  return (
    <Div styles={styles}>
      <div className="chat-action" onClick={handlePlayAudio}>
        <>
          <p>
            Audio Recording <span> {showAudioPlayer ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}</span>
          </p>
          {isLoadingPlayAudio && <LoadingAnimation />}
          <div className="d-flex gap-1">
            <Button
              variant="primary"
              radius="rounded"
              onClick={handleDownloadRecording}
              disabled={isLoadingRecording || call.jobType === CallTypeToJobTypeEnum.APIIntegration}
            >
              {isLoadingRecording ? <LoadingAnimation /> : <img src={PaperDownloadIcon} alt="Download" />}
              Download
            </Button>
          </div>
        </>
      </div>

      {showAudioPlayer && (
        <div className="chat-action audio-player-container">
          <PostCallAudioPlayer isLoadingPlayAudio={isLoadingPlayAudio} audioSrc={recordingSrc} />
        </div>
      )}

      <div className="chat-action">
        <div>Transcript</div>
        <div className="d-flex gap-1">
          <Button
            variant="primary"
            radius="rounded"
            size="small"
            disabled={call.jobType === CallTypeToJobTypeEnum.APIIntegration}
            onClick={handelCopyTrasnscript}
          >
            <FaCopy /> Copy
          </Button>
          <Button
            variant="primary"
            radius="rounded"
            size="small"
            disabled={call.jobType === CallTypeToJobTypeEnum.APIIntegration}
            onClick={handelDownloadTranscriptTxt}
          >
            <FaFileAlt /> .txt
          </Button>
          <Button
            variant="primary"
            size="small"
            disabled={call.jobType === CallTypeToJobTypeEnum.APIIntegration}
            radius="rounded"
            onClick={handleDownloadTranscript}
          >
            <FaFileCode />
            .json
          </Button>
        </div>
      </div>
      <div className="transcript" id="post-call-transcript">
        {chatTranscript && (
          <CallChatDisplay
            call={call}
            transcript={chatTranscript.data}
            claim={claim}
            callStartTime={callStartTime}
            collapseLongMessages
          />
        )}
        {chatTranscript === null && (
          <div className="notransscript">Transcript and audio are not available for this call.</div>
        )}
      </div>
    </Div>
  )
}

const Div = styled.div<{ styles: any }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid ${({ theme }) => theme.colors.grey.lighter};

  > .chat-action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey.lighter};
    color: ${({ theme }) => theme.colors.primary.darker};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    min-height: ${({ theme }) => theme.util.pixelToRem(61)};
  }

  > .chat-action:first-child {
    p span {
      color: ${({ theme }) => theme.colors.primary.darker};
    }
    cursor: pointer;
  }

  > .audio-player-container {
    animation: ${reveal} 1s;
  }

  .notransscript {
    margin-top: 3rem;
    margin-bottom: 3rem;
    margin-left: 1rem;
    margin-right: 1rem;
    padding: 1rem;
    display: grid;
    grid-template-columns: 1fr;
    border-radius: 5px;
    border: solid 2px #ffd870;
    background-color: #fff2cf;
    font-family: ${({ theme }) => theme.fontFamily.milliard};
    padding: ${({ theme }) => theme.util.pixelToRem(10)} 0px ${({ theme }) => theme.typography.s.normal};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    text-align: center;
    color: ${({ theme }) => theme.colors.primary.darker};
  }

  > .transcript {
    background-color: ${({ theme }) => theme.colors.tertiary.lightest};
    height: calc(100% - ${({ theme }) => theme.util.pixelToRem(61)} - ${({ theme }) => theme.util.pixelToRem(61)});
    overflow: hidden;
  }

  ${({ styles }) =>
    styles
      ? css`
          ${styles}
        `
      : null};
`
